import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroup } from '@angular/forms';
import { ISelect } from '@tecalisdev/infinity';
import { EMPTY, Observable, Subject, catchError, of, switchMap, takeUntil } from 'rxjs';
import { IFormFieldConfig, IFormStepConfig } from '../models/user-identification';
import { IProvince, IProvincesResponse } from '../services/province.service';
import {
	coerceProvinceSelectField,
	stepHasCountryProvinceCascade,
} from './dynamic-form-field.util';
import {
	isCountryFormField,
	isProvinceFormField,
	usesFormFieldSelectSearch,
} from './form-field-options.util';

/** Estado UI del select de provincia tras cascada. */
export type ProvinceSelectUiState = {
	options: ISelect[];
	search: boolean;
	disabled: boolean;
};

export type CountryProvinceCascadeBindings = {
	stepForm: FormGroup;
	/** Emite al reconstruir el paso o destruir el componente. */
	until$: Observable<unknown>;
	destroyRef: DestroyRef;
	loadProvinces: (countryCode: string) => Observable<IProvincesResponse>;
	onHttpError: (error: HttpErrorResponse) => void;
	/** Actualiza options/search/disabled en el host y sincroniza el FormControl. */
	applyProvinceState: (
		provinceField: IFormFieldConfig,
		state: ProvinceSelectUiState,
		config: { resetValue: boolean },
	) => void;
	markForCheck: () => void;
	/** Label i18n opcional; por defecto usa `province.name`. */
	resolveProvinceLabel?: (province: IProvince) => string;
};

/** Extrae el id de un `tec-select` (objeto ISelect o string). */
export function extractDynamicFormSelectValue(raw: unknown): string {
	if (raw && typeof raw === 'object' && 'id' in (raw as object)) {
		const id = (raw as { id?: unknown }).id;
		return id === null || id === undefined ? '' : String(id);
	}
	if (raw === null || raw === undefined) {
		return '';
	}
	return String(raw);
}

/** Mapea respuesta API → opciones de select (valor persistido = `name`). */
export function mapProvincesToSelectOptions(
	provinces: IProvince[],
	resolveLabel?: (province: IProvince) => string,
): ISelect[] {
	return provinces.map((province) => ({
		id: province.name,
		label: resolveLabel?.(province) ?? province.name,
	}));
}

export function buildProvinceSelectUiState(
	provinceField: IFormFieldConfig,
	options: ISelect[],
): ProvinceSelectUiState {
	return {
		options,
		search: usesFormFieldSelectSearch(coerceProvinceSelectField(provinceField, true), options.length),
		disabled: options.length < 1,
	};
}

/** Aplica valor/enable al control de provincia según opciones cargadas. */
export function syncProvinceFormControl(
	control: AbstractControl,
	options: ISelect[],
	config: { resetValue: boolean },
): void {
	if (config.resetValue) {
		control.setValue('', { emitEvent: false });
		// Evita error «obligatorio» al cambiar de país antes de que el usuario elija provincia.
		control.markAsPristine();
		control.markAsUntouched();
	} else if (options.length > 0) {
		// NIF-658: con options vacías (carga HTTP) no invalidar el prefill OCR.
		const current = extractDynamicFormSelectValue(control.value);
		const stillValid = options.some((option) => String(option.id) === current);
		if (current && !stillValid) {
			control.setValue('', { emitEvent: false });
			control.markAsPristine();
			control.markAsUntouched();
		}
	}

	if (options.length < 1) {
		control.disable({ emitEvent: false });
	} else {
		control.enable({ emitEvent: false });
	}
}

/**
 * Suscribe la cascada país → provincia en el paso actual.
 * Vacía opciones al lanzar cada petición; en la 1.ª carga conserva valor guardado si sigue válido.
 */
export function bindCountryProvinceCascade(
	step: IFormStepConfig | undefined,
	bindings: CountryProvinceCascadeBindings,
): void {
	if (!stepHasCountryProvinceCascade(step?.fields)) {
		return;
	}

	const countryField = step?.fields?.find((field) => isCountryFormField(field));
	const provinceField = step?.fields?.find((field) => isProvinceFormField(field));
	if (!countryField || !provinceField) {
		return;
	}

	const countryControl = bindings.stepForm.get(countryField.name);
	const provinceControl = bindings.stepForm.get(provinceField.name);
	if (!countryControl || !provinceControl) {
		return;
	}

	let pendingProvinceReset = true;
	const countryCode$ = new Subject<string>();

	const pushProvinceState = (options: ISelect[], resetValue: boolean): void => {
		const state = buildProvinceSelectUiState(provinceField, options);
		bindings.applyProvinceState(provinceField, state, { resetValue });
		syncProvinceFormControl(provinceControl, options, { resetValue });
		bindings.markForCheck();
	};

	countryCode$
		.pipe(
			switchMap((countryCode) => {
				pushProvinceState([], pendingProvinceReset || !countryCode);
				if (!countryCode) {
					return EMPTY;
				}
				return bindings.loadProvinces(countryCode).pipe(
					catchError((error: HttpErrorResponse) => {
						pendingProvinceReset = true;
						bindings.onHttpError(error);
						return of({ provinces: [] });
					}),
				);
			}),
			takeUntil(bindings.until$),
			takeUntilDestroyed(bindings.destroyRef),
		)
		.subscribe((response) => {
			const options = mapProvincesToSelectOptions(
				response.provinces ?? [],
				bindings.resolveProvinceLabel,
			);
			pushProvinceState(options, pendingProvinceReset);
			pendingProvinceReset = true;
		});

	pendingProvinceReset = false;
	countryCode$.next(extractDynamicFormSelectValue(countryControl.value));

	countryControl.valueChanges
		.pipe(takeUntil(bindings.until$), takeUntilDestroyed(bindings.destroyRef))
		.subscribe((value) => {
			pendingProvinceReset = true;
			countryCode$.next(extractDynamicFormSelectValue(value));
		});
}
